// English message catalog (source of truth for copy). Keys are namespaced by
// screen/domain. es.js must mirror every key here; a missing es key falls back
// to English via i18n `fallbackLocale`.
export default {
  // Shared copy reused across screens (Checkout, Customer, …). One translation,
  // many call sites.
  common: {
    customer: 'Customer',
    deliverTo: 'Deliver order to',
    orderDetails: 'Order Details',
    placeOrder: 'Place Order',
    deliveryUnavailableAddress: 'Delivery is not available to the selected address',
    noteLabel: 'Add a note to your order:',
    notePlaceholder: 'e.g. No onions, please.',
    addInstructions: 'Add Special Instructions',
    hideInstructions: 'Hide Instructions',
    returnToMenu: 'Return to menu',
  },
  cart: {
    title: 'Your Order',
    empty: 'Cart is empty',
    checkout: 'Checkout',
    customer: 'Customer',
    qty: 'x {qty}',
    plusTax: '+tax',
    label: 'Cart',
  },
  a11y: {
    businessLogo: 'business logo',
    storeLogo: 'store logo',
    bgImage: 'background image',
  },
  fields: {
    address: 'Address',
    city: 'City',
    stateProvince: 'State / Province',
    zip: 'Zip',
    postalCode: 'Postal code',
    firstName: 'First name',
    lastName: 'Last name',
    mobilePhone: 'Mobile phone number',
    emailAddress: 'Email address',
    startTypingAddress: 'Start typing your address',
    enterAddress: 'Enter an address',
    edit: 'Edit',
  },
  deliveryField: {
    tryAgain: 'Try again',
    label: 'Delivery:',
    // Named interpolation: number precedes the unit in both locales.
    etaMin: '~{eta} min',
    feeEta: '{fee} · ~{eta} min',
  },
  datetime: {
    selectDate: 'Select date',
    selectTime: 'Select time',
    selectDatePlaceholder: 'Select a date',
    selectDateLabel: 'Select Date',
    selectTimeLabel: 'Select Time',
    today: 'Today',
    tomorrow: 'Tomorrow',
  },
  item: {
    specialInstructions: 'Special Instructions',
    instructionsPlaceholder: "(e.g. Please don't include plastic utensils)",
    addToOrder: 'Add to order',
    optional: '(optional)',
    requiredSelectOne: 'Required, select only one',
    // Interpolated modifier-count phrases (whole phrase translated so word
    // order is the translator's to control).
    minRequiredMaxAllowed: '{min} required, {max} max',
    requiredMinOrMore: 'Required {min} or more',
    maxAllowed: '{max} max',
  },
  total: {
    subtotal: 'Subtotal',
    deliveryCharge: 'Delivery Charge',
    estimatedTax: 'Estimated Tax',
    totalCard: 'Total Card',
    totalCash: 'Total Cash',
    total: 'Total',
  },
  paymentForm: {
    cardNumberRequired: 'Card number is required',
    cardHolderRequired: 'Cardholder name is required',
    cardExpiryRequired: 'Card expiry is required',
    cardCvvRequired: 'Card CVV is required',
    cardPostalRequired: 'Card postal code is required',
    cardStreetRequired: 'Cardholder street address is required',
  },
  payment: {
    cardError: 'There was an issue with processing your card. Please try again.',
    payForOrder: 'Pay For An Order',
    payNow: 'Pay Now',
    card: 'Card',
    cash: 'Cash',
    selectOption: 'Please select a payment option',
    confirm: 'Confirm',
  },
  orderPayment: {
    statePrefix: 'Order Payment State:',
    loading: 'Loading...',
    requestFailed: 'Request failed',
    unpaid: 'Unpaid:',
  },
  customerCard: {
    notYou: "It's not you?",
    reset: 'Reset customer data',
  },
  hours: {
    open: 'Open',
    closed: 'Closed',
    title: 'Opening Hours',
  },
  // Weekday display names, keyed by the 3-letter key derived from the English
  // server day name (see dayNameToKey). Comparison still uses the raw English
  // name; only the DISPLAY is localized, so matching never breaks.
  weekdays: {
    mon: 'Monday',
    tue: 'Tuesday',
    wed: 'Wednesday',
    thu: 'Thursday',
    fri: 'Friday',
    sat: 'Saturday',
    sun: 'Sunday',
  },
  menu: {
    scrollLeft: 'Scroll left',
    categoryNav: 'Category Navigation',
    scrollRight: 'Scroll right',
    noItemsYet: 'No items yet',
    noItemsCategory: 'No items in this category',
    loading: 'Loading...',
  },
  orderType: {
    priceAfterAddress: 'Price after address',
    noServices: 'NO SERVICES ENABLED',
    flatFee: '{price} delivery',
  },
  delivery: {
    unavailableAddress: 'Delivery isn’t available to this address',
    checkingAvailability: 'Checking delivery availability…',
    pricingUnavailable: 'Live delivery pricing is temporarily unavailable',
    calculatedAfterAddress: 'Calculated after address',
  },
  // Form field labels (feed yup's built-in messages) + custom validation copy.
  validation: {
    labels: {
      specialInstructions: 'Special Instructions',
      firstName: 'First Name',
      lastName: 'Last Name',
      phoneNumber: 'Phone Number',
      email: 'Email',
      address: 'Address',
      city: 'City',
      state: 'State or Province',
      postalCode: 'Postal code',
    },
    phoneFormat: 'Phone format +1 (XXX) XXX-XXXX',
    phoneInvalid: 'Enter a valid phone number',
    addressRequired: 'Address is required',
    cityRequired: 'City is required',
    stateRequired: 'State is required',
    zipRequired: 'ZIP is required',
    zipIncorrect: 'Incorrect ZIP',
  },
  schedule: {
    selectDate: 'Please select a date',
    dateUnavailable: 'Selected date is not available',
    datePast: 'Selected date is in the past',
    selectTime: 'Please select a time',
    timeRange: 'Time must be between 0 and 1440 minutes',
    slotUnavailable: 'Selected time slot is not available',
    timePast: 'Selected time has already passed',
    outsideHours: 'Time is outside business hours',
    asap: 'ASAP',
    // Pluralized: vue-i18n picks the form by the count passed as the 2nd arg.
    maxDaysAhead: 'Date cannot be more than {n} day ahead | Date cannot be more than {n} days ahead',
  },
  checkout: {
    uberAsapNote: 'Delivery as soon as possible — your courier is dispatched right after checkout.',
    payment: 'Payment',
    checkThese: 'Check these things:',
    checkNext: 'Check next things:',
    dateTimeRequired: 'Date/Time data is required',
    validPaymentRequired: 'Valid payment data is required',
    cardError: 'There was an issue with processing your card. Please try again.',
  },
  thankyou: {
    title: 'Thank you for your order!',
    orderNumber: 'Order #',
    payment: 'Payment:',
    paymentLink: 'You can check the payment status and complete the payment here:',
    checkPaymentState: 'Check Payment State',
    desc: 'We will call you shortly to confirm the details or your order will be automatically processed.',
    trackingTitle: 'Delivery tracking',
    status: 'Status:',
    courier: 'Courier:',
    trackDelivery: 'Track your delivery',
    doNotChangePayment: '*Please do not change the payment method.',
    service: 'Service',
    date: 'Date',
    deliveryAddress: 'Delivery Address',
    pickUpAt: 'You can pick up your order at',
    paymentMethod: 'Payment Method',
    orderSummary: 'Order Summary',
    returnToMenu: 'Return to menu',
    // Uber courier status labels (keyed by ledger status).
    courierStatus: {
      pending: 'Preparing',
      pickup: 'Courier on the way to the store',
      pickup_complete: 'Picked up',
      dropoff: 'Out for delivery',
      delivered: 'Delivered',
      canceled: 'Canceled',
      returned: 'Returned',
      failed: 'Delivery failed',
      uber_failed: 'Arranging delivery',
    },
  },
};
